import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { AutoRefreshToolbar } from '@/components/common';
import ActivityTimeline from '@/components/activity/ActivityTimeline';
import AttentionPanel from '@/components/activity/AttentionPanel';
import Pagination from '@/components/Pagination';
import { ErrorBanner, FilterSelect, Toolbar } from '@/components/ui';
import { customerActivityService, type CustomerActivityItem } from '@/api/services/customerActivity';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import { applyMarkAllRead, applyMarkRead } from '@/utils/customerActivityState';
import { subscribeCustomerActivityRefresh, invalidateCustomerActivityRefresh } from '@/utils/customerActivityRefresh';
import { tActivity } from '@/i18n/activity';
import { useMe } from '@/context/MeContext';
import { canViewWorkspaceActivity } from '@/utils/navCapabilities';
import { resolveActivityActionCapabilities } from '@/utils/activityActions';
import { customerActivityCategoriesForUser } from '@/utils/activityVisibility';
import { Toggle } from '@/components/ui';

const SEVERITIES = ['', 'info', 'success', 'warning', 'error', 'critical'];

export default function CustomerActivityPage() {
  const { me } = useMe();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState<CustomerActivityItem[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const searchId = searchParams.get('search_id') || '';

  const [activityRefreshEpoch, setActivityRefreshEpoch] = useState(0);

  const fetchPage = useCallback(async () => {
    const data = await customerActivityService.list({
      page,
      page_size: 20,
      category: category || undefined,
      severity: severity || undefined,
      unread_only: unreadOnly,
      mine_only: mineOnly,
      search_id: searchId || undefined,
    });
    setItems(data.items);
    setPages(data.pagination.pages);
    setTotal(data.pagination.total);
    setUnreadCount(data.summary.unreadCount);
  }, [page, category, severity, unreadOnly, mineOnly, searchId]);

  const { autoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: () => items.length > 0,
    load: async () => {
      await fetchPage();
    },
    autoRefresh: {
      intervalMs: 15000,
      deps: [page, category, severity, unreadOnly, mineOnly, searchId],
      storageKey: 'customer-activity-auto-refresh',
    },
  });

  useEffect(() => {
    return subscribeCustomerActivityRefresh(() => {
      void fetchPage().then(() => {
        setActivityRefreshEpoch((value) => value + 1);
      });
    });
  }, [fetchPage]);

  async function markRead(id: string) {
    setItems((prev) => applyMarkRead(prev, id));
    setUnreadCount((count) => Math.max(0, count - 1));
    try {
      await customerActivityService.markRead(id);
      invalidateCustomerActivityRefresh();
      await autoRefresh.refresh();
    } catch {
      await autoRefresh.refresh();
    }
  }

  async function markAllRead() {
    setItems((prev) => applyMarkAllRead(prev));
    setUnreadCount(0);
    try {
      await customerActivityService.markAllRead();
      invalidateCustomerActivityRefresh();
      await autoRefresh.refresh();
    } catch {
      await autoRefresh.refresh();
    }
  }

  if (!canViewWorkspaceActivity(me)) {
    return <Navigate to="/" replace />;
  }

  const categories = customerActivityCategoriesForUser(me);
  const actionCapabilities = resolveActivityActionCapabilities(me);

  return (
    <div className="customer-activity-page">
      <PageHeader title={tActivity('activity.page.title')} subtitle={tActivity('activity.page.subtitle')}>
        <AutoRefreshToolbar control={autoRefresh} intervalLabel="Every 15s" />
        {unreadCount > 0 ? (
          <button type="button" className="btn btn-ghost" onClick={() => void markAllRead()}>
            {tActivity('activity.markAllRead')} ({unreadCount})
          </button>
        ) : null}
      </PageHeader>

      {autoRefresh.error ? <ErrorBanner message={autoRefresh.error} /> : null}

      <AttentionPanel refreshSignal={activityRefreshEpoch} />

      <Toolbar>
        <FilterSelect label={tActivity('activity.filter.category')} htmlFor="activity-category">
          <select id="activity-category" value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
            {categories.map((c) => (
              <option key={c || 'all'} value={c}>
                {c || 'All categories'}
              </option>
            ))}
          </select>
        </FilterSelect>
        <FilterSelect label={tActivity('activity.filter.severity')} htmlFor="activity-severity">
          <select id="activity-severity" value={severity} onChange={(e) => { setSeverity(e.target.value); setPage(1); }}>
            {SEVERITIES.map((s) => (
              <option key={s || 'all'} value={s}>
                {s || 'All severities'}
              </option>
            ))}
          </select>
        </FilterSelect>
        <Toggle
          compact
          checked={unreadOnly}
          onChange={(next) => {
            setUnreadOnly(next);
            setPage(1);
          }}
          label={tActivity('activity.filter.unread')}
        />
        <Toggle
          compact
          checked={mineOnly}
          onChange={(next) => {
            setMineOnly(next);
            setPage(1);
          }}
          label={tActivity('activity.filter.mine')}
        />
      </Toolbar>

      <ActivityTimeline
        items={items}
        loading={initialLoading}
        onMarkRead={(id) => void markRead(id)}
        actionCapabilities={actionCapabilities}
      />

      <Pagination page={page} pages={pages} total={total} onPage={setPage} />
    </div>
  );
}
